/**
 * Language constants for OpenAI and Azure OpenAI Realtime APIs
 * 
 * OpenAI Realtime API uses Whisper for transcription which supports 98 languages
 * Azure OpenAI Realtime API uses Azure Speech Service which supports 100+ languages with locale codes
 */

export interface Language {
  code: string
  name: string
  provider: 'openai' | 'azure'
}

/**
 * OpenAI Realtime API supported languages (Whisper)
 * Uses 2-letter ISO 639-1 language codes
 * Source: https://platform.openai.com/docs/guides/speech-to-text
 */
export const OPENAI_LANGUAGES: Language[] = [
  { code: 'af', name: 'Afrikaans', provider: 'openai' },
  { code: 'ar', name: 'Arabic', provider: 'openai' },
  { code: 'hy', name: 'Armenian', provider: 'openai' },
  { code: 'az', name: 'Azerbaijani', provider: 'openai' },
  { code: 'be', name: 'Belarusian', provider: 'openai' },
  { code: 'bs', name: 'Bosnian', provider: 'openai' },
  { code: 'bg', name: 'Bulgarian', provider: 'openai' },
  { code: 'ca', name: 'Catalan', provider: 'openai' },
  { code: 'zh', name: 'Chinese', provider: 'openai' },
  { code: 'hr', name: 'Croatian', provider: 'openai' },
  { code: 'cs', name: 'Czech', provider: 'openai' },
  { code: 'da', name: 'Danish', provider: 'openai' },
  { code: 'nl', name: 'Dutch', provider: 'openai' },
  { code: 'en', name: 'English', provider: 'openai' },
  { code: 'et', name: 'Estonian', provider: 'openai' },
  { code: 'fi', name: 'Finnish', provider: 'openai' },
  { code: 'fr', name: 'French', provider: 'openai' },
  { code: 'gl', name: 'Galician', provider: 'openai' },
  { code: 'de', name: 'German', provider: 'openai' },
  { code: 'el', name: 'Greek', provider: 'openai' },
  { code: 'he', name: 'Hebrew', provider: 'openai' },
  { code: 'hi', name: 'Hindi', provider: 'openai' },
  { code: 'hu', name: 'Hungarian', provider: 'openai' },
  { code: 'is', name: 'Icelandic', provider: 'openai' },
  { code: 'id', name: 'Indonesian', provider: 'openai' },
  { code: 'it', name: 'Italian', provider: 'openai' },
  { code: 'ja', name: 'Japanese', provider: 'openai' },
  { code: 'kn', name: 'Kannada', provider: 'openai' },
  { code: 'kk', name: 'Kazakh', provider: 'openai' },
  { code: 'ko', name: 'Korean', provider: 'openai' },
  { code: 'lv', name: 'Latvian', provider: 'openai' },
  { code: 'lt', name: 'Lithuanian', provider: 'openai' },
  { code: 'mk', name: 'Macedonian', provider: 'openai' },
  { code: 'ms', name: 'Malay', provider: 'openai' },
  { code: 'mr', name: 'Marathi', provider: 'openai' },
  { code: 'mi', name: 'Maori', provider: 'openai' },
  { code: 'ne', name: 'Nepali', provider: 'openai' },
  { code: 'no', name: 'Norwegian', provider: 'openai' },
  { code: 'fa', name: 'Persian', provider: 'openai' },
  { code: 'pl', name: 'Polish', provider: 'openai' },
  { code: 'pt', name: 'Portuguese', provider: 'openai' },
  { code: 'ro', name: 'Romanian', provider: 'openai' },
  { code: 'ru', name: 'Russian', provider: 'openai' },
  { code: 'sr', name: 'Serbian', provider: 'openai' },
  { code: 'sk', name: 'Slovak', provider: 'openai' },
  { code: 'sl', name: 'Slovenian', provider: 'openai' },
  { code: 'es', name: 'Spanish', provider: 'openai' },
  { code: 'sw', name: 'Swahili', provider: 'openai' },
  { code: 'sv', name: 'Swedish', provider: 'openai' },
  { code: 'tl', name: 'Tagalog', provider: 'openai' },
  { code: 'ta', name: 'Tamil', provider: 'openai' },
  { code: 'th', name: 'Thai', provider: 'openai' },
  { code: 'tr', name: 'Turkish', provider: 'openai' },
  { code: 'uk', name: 'Ukrainian', provider: 'openai' },
  { code: 'ur', name: 'Urdu', provider: 'openai' },
  { code: 'vi', name: 'Vietnamese', provider: 'openai' },
  { code: 'cy', name: 'Welsh', provider: 'openai' },
]

/**
 * Azure OpenAI Realtime API supported languages (Azure Speech Service)
 * Uses full locale codes (e.g., en-US, ms-MY)
 * Source: https://learn.microsoft.com/en-us/azure/ai-services/speech-service/language-support
 */
export const AZURE_LANGUAGES: Language[] = [
  { code: 'af-ZA', name: 'Afrikaans (South Africa)', provider: 'azure' },
  { code: 'am-ET', name: 'Amharic (Ethiopia)', provider: 'azure' },
  { code: 'ar-AE', name: 'Arabic (United Arab Emirates)', provider: 'azure' },
  { code: 'ar-BH', name: 'Arabic (Bahrain)', provider: 'azure' },
  { code: 'ar-DZ', name: 'Arabic (Algeria)', provider: 'azure' },
  { code: 'ar-EG', name: 'Arabic (Egypt)', provider: 'azure' },
  { code: 'ar-IQ', name: 'Arabic (Iraq)', provider: 'azure' },
  { code: 'ar-JO', name: 'Arabic (Jordan)', provider: 'azure' },
  { code: 'ar-KW', name: 'Arabic (Kuwait)', provider: 'azure' },
  { code: 'ar-LB', name: 'Arabic (Lebanon)', provider: 'azure' },
  { code: 'ar-LY', name: 'Arabic (Libya)', provider: 'azure' },
  { code: 'ar-MA', name: 'Arabic (Morocco)', provider: 'azure' },
  { code: 'ar-OM', name: 'Arabic (Oman)', provider: 'azure' },
  { code: 'ar-QA', name: 'Arabic (Qatar)', provider: 'azure' },
  { code: 'ar-SA', name: 'Arabic (Saudi Arabia)', provider: 'azure' },
  { code: 'ar-SY', name: 'Arabic (Syria)', provider: 'azure' },
  { code: 'ar-TN', name: 'Arabic (Tunisia)', provider: 'azure' },
  { code: 'ar-YE', name: 'Arabic (Yemen)', provider: 'azure' },
  { code: 'bg-BG', name: 'Bulgarian (Bulgaria)', provider: 'azure' },
  { code: 'bn-IN', name: 'Bengali (India)', provider: 'azure' },
  { code: 'ca-ES', name: 'Catalan (Spain)', provider: 'azure' },
  { code: 'cs-CZ', name: 'Czech (Czechia)', provider: 'azure' },
  { code: 'da-DK', name: 'Danish (Denmark)', provider: 'azure' },
  { code: 'de-AT', name: 'German (Austria)', provider: 'azure' },
  { code: 'de-CH', name: 'German (Switzerland)', provider: 'azure' },
  { code: 'de-DE', name: 'German (Germany)', provider: 'azure' },
  { code: 'el-GR', name: 'Greek (Greece)', provider: 'azure' },
  { code: 'en-AU', name: 'English (Australia)', provider: 'azure' },
  { code: 'en-CA', name: 'English (Canada)', provider: 'azure' },
  { code: 'en-GB', name: 'English (United Kingdom)', provider: 'azure' },
  { code: 'en-HK', name: 'English (Hong Kong SAR)', provider: 'azure' },
  { code: 'en-IE', name: 'English (Ireland)', provider: 'azure' },
  { code: 'en-IN', name: 'English (India)', provider: 'azure' },
  { code: 'en-KE', name: 'English (Kenya)', provider: 'azure' },
  { code: 'en-NG', name: 'English (Nigeria)', provider: 'azure' },
  { code: 'en-NZ', name: 'English (New Zealand)', provider: 'azure' },
  { code: 'en-PH', name: 'English (Philippines)', provider: 'azure' },
  { code: 'en-SG', name: 'English (Singapore)', provider: 'azure' },
  { code: 'en-TZ', name: 'English (Tanzania)', provider: 'azure' },
  { code: 'en-US', name: 'English (United States)', provider: 'azure' },
  { code: 'en-ZA', name: 'English (South Africa)', provider: 'azure' },
  { code: 'es-AR', name: 'Spanish (Argentina)', provider: 'azure' },
  { code: 'es-BO', name: 'Spanish (Bolivia)', provider: 'azure' },
  { code: 'es-CL', name: 'Spanish (Chile)', provider: 'azure' },
  { code: 'es-CO', name: 'Spanish (Colombia)', provider: 'azure' },
  { code: 'es-CR', name: 'Spanish (Costa Rica)', provider: 'azure' },
  { code: 'es-CU', name: 'Spanish (Cuba)', provider: 'azure' },
  { code: 'es-DO', name: 'Spanish (Dominican Republic)', provider: 'azure' },
  { code: 'es-EC', name: 'Spanish (Ecuador)', provider: 'azure' },
  { code: 'es-ES', name: 'Spanish (Spain)', provider: 'azure' },
  { code: 'es-GT', name: 'Spanish (Guatemala)', provider: 'azure' },
  { code: 'es-HN', name: 'Spanish (Honduras)', provider: 'azure' },
  { code: 'es-MX', name: 'Spanish (Mexico)', provider: 'azure' },
  { code: 'es-NI', name: 'Spanish (Nicaragua)', provider: 'azure' },
  { code: 'es-PA', name: 'Spanish (Panama)', provider: 'azure' },
  { code: 'es-PE', name: 'Spanish (Peru)', provider: 'azure' },
  { code: 'es-PR', name: 'Spanish (Puerto Rico)', provider: 'azure' },
  { code: 'es-PY', name: 'Spanish (Paraguay)', provider: 'azure' },
  { code: 'es-SV', name: 'Spanish (El Salvador)', provider: 'azure' },
  { code: 'es-US', name: 'Spanish (United States)', provider: 'azure' },
  { code: 'es-UY', name: 'Spanish (Uruguay)', provider: 'azure' },
  { code: 'es-VE', name: 'Spanish (Venezuela)', provider: 'azure' },
  { code: 'et-EE', name: 'Estonian (Estonia)', provider: 'azure' },
  { code: 'fi-FI', name: 'Finnish (Finland)', provider: 'azure' },
  { code: 'fil-PH', name: 'Filipino (Philippines)', provider: 'azure' },
  { code: 'fr-BE', name: 'French (Belgium)', provider: 'azure' },
  { code: 'fr-CA', name: 'French (Canada)', provider: 'azure' },
  { code: 'fr-CH', name: 'French (Switzerland)', provider: 'azure' },
  { code: 'fr-FR', name: 'French (France)', provider: 'azure' },
  { code: 'gu-IN', name: 'Gujarati (India)', provider: 'azure' },
  { code: 'he-IL', name: 'Hebrew (Israel)', provider: 'azure' },
  { code: 'hi-IN', name: 'Hindi (India)', provider: 'azure' },
  { code: 'hr-HR', name: 'Croatian (Croatia)', provider: 'azure' },
  { code: 'hu-HU', name: 'Hungarian (Hungary)', provider: 'azure' },
  { code: 'id-ID', name: 'Indonesian (Indonesia)', provider: 'azure' },
  { code: 'is-IS', name: 'Icelandic (Iceland)', provider: 'azure' },
  { code: 'it-IT', name: 'Italian (Italy)', provider: 'azure' },
  { code: 'ja-JP', name: 'Japanese (Japan)', provider: 'azure' },
  { code: 'kn-IN', name: 'Kannada (India)', provider: 'azure' },
  { code: 'ko-KR', name: 'Korean (Korea)', provider: 'azure' },
  { code: 'lt-LT', name: 'Lithuanian (Lithuania)', provider: 'azure' },
  { code: 'lv-LV', name: 'Latvian (Latvia)', provider: 'azure' },
  { code: 'mk-MK', name: 'Macedonian (North Macedonia)', provider: 'azure' },
  { code: 'ml-IN', name: 'Malayalam (India)', provider: 'azure' },
  { code: 'mr-IN', name: 'Marathi (India)', provider: 'azure' },
  { code: 'ms-MY', name: 'Malay (Malaysia)', provider: 'azure' },
  { code: 'nb-NO', name: 'Norwegian Bokmål (Norway)', provider: 'azure' },
  { code: 'nl-BE', name: 'Dutch (Belgium)', provider: 'azure' },
  { code: 'nl-NL', name: 'Dutch (Netherlands)', provider: 'azure' },
  { code: 'pl-PL', name: 'Polish (Poland)', provider: 'azure' },
  { code: 'pt-BR', name: 'Portuguese (Brazil)', provider: 'azure' },
  { code: 'pt-PT', name: 'Portuguese (Portugal)', provider: 'azure' },
  { code: 'ro-RO', name: 'Romanian (Romania)', provider: 'azure' },
  { code: 'ru-RU', name: 'Russian (Russia)', provider: 'azure' },
  { code: 'sk-SK', name: 'Slovak (Slovakia)', provider: 'azure' },
  { code: 'sl-SI', name: 'Slovenian (Slovenia)', provider: 'azure' },
  { code: 'sr-RS', name: 'Serbian (Cyrillic, Serbia)', provider: 'azure' },
  { code: 'sv-SE', name: 'Swedish (Sweden)', provider: 'azure' },
  { code: 'sw-KE', name: 'Swahili (Kenya)', provider: 'azure' },
  { code: 'ta-IN', name: 'Tamil (India)', provider: 'azure' },
  { code: 'te-IN', name: 'Telugu (India)', provider: 'azure' },
  { code: 'th-TH', name: 'Thai (Thailand)', provider: 'azure' },
  { code: 'tr-TR', name: 'Turkish (Türkiye)', provider: 'azure' },
  { code: 'uk-UA', name: 'Ukrainian (Ukraine)', provider: 'azure' },
  { code: 'ur-PK', name: 'Urdu (Pakistan)', provider: 'azure' },
  { code: 'vi-VN', name: 'Vietnamese (Vietnam)', provider: 'azure' },
  { code: 'zh-CN', name: 'Chinese (Mandarin, Simplified)', provider: 'azure' },
  { code: 'zh-HK', name: 'Chinese (Cantonese, Traditional)', provider: 'azure' },
  { code: 'zh-TW', name: 'Chinese (Taiwanese Mandarin, Traditional)', provider: 'azure' },
]

/**
 * Get languages for a specific provider
 */
export function getLanguagesForProvider(provider: 'openai' | 'azure'): Language[] {
  return provider === 'openai' ? OPENAI_LANGUAGES : AZURE_LANGUAGES
}

/**
 * Validate if a language code is supported by a provider
 */
export function isLanguageSupported(provider: 'openai' | 'azure', languageCode: string): boolean {
  const languages = getLanguagesForProvider(provider)
  return languages.some(lang => lang.code === languageCode)
}

/**
 * Get language name by code and provider
 */
export function getLanguageName(provider: 'openai' | 'azure', languageCode: string): string | null {
  const languages = getLanguagesForProvider(provider)
  const language = languages.find(lang => lang.code === languageCode)
  return language ? language.name : null
}

